import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Typography,
  Grid,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Activity, Save, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '../../lib/api';
import { Appointment } from '../../types';
import { safeConsole } from '../../lib/piiProtection';
import { IndicatorBadge } from './vitalIndicators';
import { DynamicEncounterFields, encodeEncounterFieldValues } from '../common/DynamicEncounterFields';
import { useSpecialtyTemplate } from '../../hooks/useSpecialtyTemplate';

interface VisitVitalsFormProps {
  patientId: string;
  appointmentId?: string;
  appointment?: Appointment;
  onSuccess?: (visitId: string) => void;
  onCancel?: () => void;
}

/**
 * VisitVitalsForm - Comprehensive vital signs and clinical info capture
 *
 * Section 1: Vitals
 * - BP (Systolic/Diastolic), Pulse, Temperature, SpO2, Respiratory Rate, Weight
 *
 * Section 2: Patient Context
 * - Chief Complaint, Duration, Current Medications
 *
 * Section 3: Clinical Notes
 * - Free text observations
 */
export function VisitVitalsForm({
  patientId,
  appointment,
  onSuccess,
  onCancel,
}: VisitVitalsFormProps) {
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [extraFieldValues, setExtraFieldValues] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    // Vitals
    systolicBP: '',
    diastolicBP: '',
    pulse: '',
    temperature: '',
    spO2: '',
    respiratoryRate: '',
    weight: '',
    // Patient Context
    chiefComplaint: appointment?.reason || '',
    duration: '',
    currentMedications: '',
    // Clinical Notes
    clinicalNotes: '',
  });

  // Falls back to the logged-in doctor's own department when no appointment.doctorId is available
  // (most patient windows across the app open with no appointment context at all).
  const { template: encounterTemplate } = useSpecialtyTemplate((appointment as any)?.doctorId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (!formData.systolicBP) {
      newErrors.systolicBP = 'Systolic BP is required';
    } else {
      const sbp = parseInt(formData.systolicBP);
      if (sbp < 60 || sbp > 200) newErrors.systolicBP = 'Allowed: 60 – 200 mmHg';
    }

    if (!formData.diastolicBP) {
      newErrors.diastolicBP = 'Diastolic BP is required';
    } else {
      const dbp = parseInt(formData.diastolicBP);
      if (dbp < 40 || dbp > 120) newErrors.diastolicBP = 'Allowed: 40 – 120 mmHg';
    }

    if (!formData.pulse) {
      newErrors.pulse = 'Pulse is required';
    } else {
      const pulse = parseInt(formData.pulse);
      if (pulse < 40 || pulse > 180) newErrors.pulse = 'Allowed: 40 – 180 bpm';
    }

    if (!formData.temperature) {
      newErrors.temperature = 'Temperature is required';
    } else {
      const temp = parseFloat(formData.temperature);
      if (temp < 90 || temp > 110) newErrors.temperature = 'Allowed: 90°F – 110°F';
    }

    if (formData.spO2) {
      const spo2 = parseInt(formData.spO2);
      if (spo2 < 70 || spo2 > 100) newErrors.spO2 = 'Allowed: 70 – 100%';
    }

    if (formData.respiratoryRate) {
      const rr = parseInt(formData.respiratoryRate);
      if (rr < 8 || rr > 60) newErrors.respiratoryRate = 'Allowed: 8 – 60 breaths/min';
    }

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      toast.error('Please fix all validation errors');
      return;
    }

    setLoading(true);
    setFieldErrors({});
    try {
      const visitPayload = {
        patientId,
        doctorId: (appointment as any)?.doctorId,
        visitDate: new Date().toISOString().split('T')[0],
        systolicBP: parseInt(formData.systolicBP),
        diastolicBP: parseInt(formData.diastolicBP),
        pulse: parseInt(formData.pulse),
        temperature: parseFloat(formData.temperature),
        spO2: formData.spO2 ? parseInt(formData.spO2) : null,
        respiratoryRate: formData.respiratoryRate ? parseInt(formData.respiratoryRate) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        chiefComplaint: formData.chiefComplaint,
        duration: formData.duration,
        currentMedications: formData.currentMedications,
        clinicalNotes: formData.clinicalNotes,
        diagnosis: '',
        structuredDataJson: encounterTemplate ? encodeEncounterFieldValues(extraFieldValues) : undefined,
      };

      const response = await api.visits.create(visitPayload);

      if (response.error) throw new Error(response.error);

      toast.success('Visit vitals recorded successfully');
      safeConsole.log('Visit created:', response.data);

      if (onSuccess && response.data?.id) onSuccess(response.data.id);

      setFormData({
        systolicBP: '',
        diastolicBP: '',
        pulse: '',
        temperature: '',
        spO2: '',
        respiratoryRate: '',
        weight: '',
        chiefComplaint: appointment?.reason || '',
        duration: '',
        currentMedications: '',
        clinicalNotes: '',
      });
      setExtraFieldValues({});
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to record vitals';
      toast.error(errorMsg);
      safeConsole.error('Failed to create visit:', error);
    } finally {
      setLoading(false);
    }
  };

  const field = (key: keyof typeof formData) => ({
    value: formData[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({ ...prev, [key]: e.target.value }));
      setFieldErrors(prev => ({ ...prev, [key]: '' }));
    },
  });

  return (
    <Card sx={{ bgcolor: 'action.hover' }}>
      <CardContent>
        <Stack spacing={2}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Activity size={20} color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Record Visit Vitals & Clinical Information
            </Typography>
          </Box>

          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {appointment
              ? `For appointment: ${appointment.doctorName || 'Doctor'} - ${appointment.reason || 'Consultation'}`
              : 'Enter vital signs and clinical information'}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {/* ===== SECTION 1: VITALS ===== */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                  🟢 Section 1: Vital Signs
                </Typography>

                {/* Systolic BP / Diastolic BP */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth label="Systolic BP *" type="number" size="small"
                      placeholder="120" disabled={loading}
                      {...field('systolicBP')}
                      error={!!fieldErrors.systolicBP}
                      helperText={fieldErrors.systolicBP || 'Allowed: 60 – 200 mmHg'}
                    />
                    <IndicatorBadge field="systolicBP" value={formData.systolicBP} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth label="Diastolic BP *" type="number" size="small"
                      placeholder="80" disabled={loading}
                      {...field('diastolicBP')}
                      error={!!fieldErrors.diastolicBP}
                      helperText={fieldErrors.diastolicBP || 'Allowed: 40 – 120 mmHg'}
                    />
                    <IndicatorBadge field="diastolicBP" value={formData.diastolicBP} />
                  </Grid>
                </Grid>

                {/* Pulse / Temperature / SpO2 */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth label="Pulse (bpm) *" type="number" size="small"
                      placeholder="72" disabled={loading}
                      {...field('pulse')}
                      error={!!fieldErrors.pulse}
                      helperText={fieldErrors.pulse || 'Allowed: 40 – 180 bpm'}
                    />
                    <IndicatorBadge field="pulse" value={formData.pulse} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth label="Temperature (°F) *" type="number" size="small"
                      inputProps={{ step: '0.1' }} placeholder="98.6" disabled={loading}
                      {...field('temperature')}
                      error={!!fieldErrors.temperature}
                      helperText={fieldErrors.temperature || 'Allowed: 90°F – 110°F'}
                    />
                    <IndicatorBadge field="temperature" value={formData.temperature} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth label="SpO₂ (%)" type="number" size="small"
                      inputProps={{ min: '70', max: '100' }} placeholder="98" disabled={loading}
                      {...field('spO2')}
                      error={!!fieldErrors.spO2}
                      helperText={fieldErrors.spO2 || 'Optional: 70 – 100%'}
                    />
                    <IndicatorBadge field="spO2" value={formData.spO2} />
                  </Grid>
                </Grid>

                {/* Respiratory Rate / Weight */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth label="Respiratory Rate (breaths/min)" type="number" size="small"
                      placeholder="16" disabled={loading}
                      {...field('respiratoryRate')}
                      error={!!fieldErrors.respiratoryRate}
                      helperText={fieldErrors.respiratoryRate || 'Optional: 8 – 60 breaths/min'}
                    />
                    <IndicatorBadge field="respiratoryRate" value={formData.respiratoryRate} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth label="Weight (kg)" type="number" size="small"
                      inputProps={{ step: '0.1' }} placeholder="70" disabled={loading}
                      {...field('weight')}
                      error={!!fieldErrors.weight}
                      helperText={fieldErrors.weight || 'Optional'}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* ===== SECTION 2: PATIENT CONTEXT ===== */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                  🟢 Section 2: Patient Context
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth label="Chief Complaint" multiline rows={2} size="small"
                      placeholder="What brings the patient in today?" disabled={loading}
                      {...field('chiefComplaint')}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth label="Duration" size="small"
                      placeholder="e.g., 2 days, 1 week" disabled={loading}
                      {...field('duration')}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth label="Current Medications" multiline rows={2} size="small"
                      placeholder="Current medications and dosages" disabled={loading}
                      {...field('currentMedications')}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* ===== SECTION 3: CLINICAL NOTES ===== */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                  🟢 Section 3: Clinical Notes
                </Typography>

                <TextField
                  fullWidth label="Clinical Notes" multiline rows={3} size="small"
                  placeholder="Observations, physical examination findings, patient history, clinical impressions..."
                  disabled={loading}
                  {...field('clinicalNotes')}
                />
              </Box>

              {encounterTemplate && (
                <DynamicEncounterFields
                  label={encounterTemplate.label}
                  vitalsExtraFields={encounterTemplate.vitalsExtraFields}
                  noteExtraFields={encounterTemplate.noteExtraFields}
                  values={extraFieldValues}
                  onChange={(key, value) => setExtraFieldValues((prev) => ({ ...prev, [key]: value }))}
                  disabled={loading}
                />
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', pt: 2 }}>
                <Button variant="outlined" size="small" startIcon={<X size={16} />} onClick={onCancel} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  variant="contained" size="small"
                  startIcon={loading ? <CircularProgress size={16} /> : <Save size={16} />}
                  onClick={handleSubmit} disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Vitals & Info'}
                </Button>
              </Stack>
            </Stack>
          </form>
        </Stack>
      </CardContent>
    </Card>
  );
}
